import { dispatch } from 'pluginServices/store';
import { pushToast } from 'containers/ToastManager/actions';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import publicationService from 'services/PublicationService';
import { sortByDate } from 'Workspace/Publications/utils';
import messages from '../messages';

const showToastError = (message) => {
  dispatch(pushToast({ message: intl.formatMessage(message) }));
};

/**
 * Fetches the assessment summary for a given publication item.
 *
 * Flow:
 *   1. Get the publication history for the publication item.
 *   2. Take the most recent publication (sorted by date).
 *   3. Use its `cohortId` / `deploymentId` to call `getDeploymentDetails`.
 *
 * Mirrors the lookup pattern used by `generateLearnspaceUrl`.
 *
 * @param {object} publicationItem
 * @returns {Promise<object|null>} deployment details payload, or `null` on error.
 */
export const fetchAssessmentSummary = async (publicationItem) => {
  try {
    const { activityId, outputType, market } = publicationItem;

    const { publications } = await publicationService.getPublicationHistory(activityId, outputType, market);

    if (!publications?.length) {
      showToastError(messages.noPublicationHistory);
      return null;
    }

    const sortedPublications = sortByDate(publications);
    const { cohortId, deploymentId } = sortedPublications[0];

    if (!cohortId || !deploymentId) {
      showToastError(messages.invalidAssessmentSummaryOption);
      return null;
    }

    const response = await publicationService.getDeploymentDetails(cohortId, deploymentId);

    if (!response || response.error) {
      showToastError(messages.errorFetchingAssessmentSummary);
      return null;
    }

    return response;
  } catch (error) {
    showToastError(messages.errorFetchingAssessmentSummary);
    return null;
  }
};
